import * as React from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Consent from "./Consent";
import PersonalInformation from "./PersonalInformation";
import {
	addressInformationVerifier,
	authorizationVerifier,
	consentVerifier,
	idVerifier,
	personalInformationVerifier,
	previousOffencesVerifier,
	selfieVerifier,
} from "../../utils/verifier";
import AddressInformation from "./AddressInformation";
import PreviousOffences from "./PreviousOffences";
import PicturesUpload from "./PicturesUpload";
import Review from "./Review";
import Authorization from "./Authorization";
import Payment from "./Payment";
import { LinearProgress } from "@mui/material";
import IdUpload from "./IdUpload";
import IdUpload2 from "./IdUpload2";
import "./formStyle.css";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
// const steps = ["Select campaign settings", "Create an ad group", "Create an ad"];

export default function RecordForm() {
	const [activeStep, setActiveStep] = React.useState(0);
	const [skipped, setSkipped] = React.useState(new Set());
	const prefilled = useSelector((state) => state.user.prefilled);
	const isStepOptional = (step) => {
		return false;
		return step === 1;
	};
	const { t } = useTranslation();
	const isStepFailed = (step) => {
		return step === 1;
	};
	const isStepSkipped = (step) => {
		return skipped.has(step);
	};
	React.useEffect(() => {
		if (prefilled) setActiveStep(1);
	}, [prefilled]);
	const handleNext = () => {
		let newSkipped = skipped;
		if (isStepSkipped(activeStep)) {
			newSkipped = new Set(newSkipped.values());
			newSkipped.delete(activeStep);
		}

		setActiveStep((prevActiveStep) => prevActiveStep + 1);
		setSkipped(newSkipped);
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleSkip = () => {
		if (!isStepOptional(activeStep)) {
			// You probably want to guard against something like this,
			// it should never occur unless someone's actively trying to break something.
			throw new Error("You can't skip a step that isn't optional.");
		}

		setActiveStep((prevActiveStep) => prevActiveStep + 1);
		setSkipped((prevSkipped) => {
			const newSkipped = new Set(prevSkipped.values());
			newSkipped.add(activeStep);
			return newSkipped;
		});
	};

	const handleReset = () => {
		setActiveStep(0);
	};
	const renderStepperControls = (handleNext, handleBack, hideNext = false, prefilled = false) => {
		return (
			<Box sx={{ display: "flex", justifyContent: hideNext ? "center" : "space-evenly", flexDirection: "row", pt: 2, mt: 4, mb: 4 }}>
				<Button
					size="large"
					color="secondary"
					variant={"contained"}
					style={{ padding: "10px 35px", borderRadius: "8px" }}
					disabled={activeStep === 0 || prefilled == true}
					onClick={handleBack}
					sx={{ mr: 1, textTransform: "none" }}
				>
					{t("Previous")}
				</Button>
				<Button
					size="large"
					onClick={handleNext}
					style={{ padding: "10px 35px", borderRadius: "8px" }}
					variant="contained"
					sx={{ textTransform: "none", display: hideNext ? "none" : "initial" }}
					color="primary"
				>
					{activeStep === steps.length - 1 ? "Finish" : t("Next")}
				</Button>
			</Box>
		);
	};
	const steps = [
		{
			label: t("Consent"),
			component: (
				<Consent prefilled={prefilled} verifier={consentVerifier} renderControls={renderStepperControls} handleNext={handleNext} handleBack={handleBack} />
			),
		},
		{
			label: t("Personal Information"),
			component: (
				<PersonalInformation
					prefilled={prefilled}
					verifier={personalInformationVerifier}
					renderControls={renderStepperControls}
					handleNext={handleNext}
					handleBack={handleBack}
				/>
			),
		},
		{
			label: t("Address Information"),
			component: (
				<AddressInformation
					prefilled={prefilled}
					verifier={addressInformationVerifier}
					renderControls={renderStepperControls}
					handleNext={handleNext}
					handleBack={handleBack}
				/>
			),
		},
		{
			label: t("Previous Offences"),
			component: (
				<PreviousOffences
					prefilled={prefilled}
					verifier={previousOffencesVerifier}
					renderControls={renderStepperControls}
					handleNext={handleNext}
					handleBack={handleBack}
				/>
			),
		},
		{
			label: t("Picture Upload"),
			component: (
				<PicturesUpload
					prefilled={prefilled}
					verifier={selfieVerifier}
					renderControls={renderStepperControls}
					handleNext={handleNext}
					handleBack={handleBack}
				/>
			),
		},
		{
			label: t("Upload First ID"),
			component: (
				<IdUpload prefilled={prefilled} verifier={idVerifier} renderControls={renderStepperControls} handleNext={handleNext} handleBack={handleBack} />
			),
		},
		{
			label: t("Upload Second ID"),
			component: (
				<IdUpload2 prefilled={prefilled} verifier={idVerifier} renderControls={renderStepperControls} handleNext={handleNext} handleBack={handleBack} />
			),
		},
		{
			label: t("Review"),
			component: (
				<Review
					prefilled={prefilled}
					verifier={personalInformationVerifier}
					renderControls={renderStepperControls}
					handleNext={handleNext}
					handleBack={handleBack}
				/>
			),
		},
		{
			label: t("Authorization and Consent"),
			component: (
				<Authorization
					prefilled={prefilled}
					verifier={authorizationVerifier}
					renderControls={renderStepperControls}
					handleNext={handleNext}
					handleBack={handleBack}
				/>
			),
		},
		{
			label: t("Payment"),
			component: (
				<Payment
					prefilled={prefilled}
					verifier={personalInformationVerifier}
					renderControls={renderStepperControls}
					handleNext={handleNext}
					handleBack={handleBack}
				/>
			),
		},
	];
	return (
		<Box>
			<LinearProgress
				variant="determinate"
				value={(activeStep * 100) / steps.length}
				style={{ marginBottom: "20px", position: "absolute", top: "101px", left: "0", width: "100%" }}
			/>
			{/* <Stepper activeStep={activeStep} style={{ marginBottom: "20px" }}>
				{steps.map((step, index) => {
					const stepProps = {};
					const labelProps = {};
					if (isStepOptional(index)) {
						labelProps.optional = <Typography variant="caption">Optional</Typography>;
					}
					if (isStepSkipped(index)) {
						stepProps.completed = false;
					}
					return (
						<Step key={step.label} {...stepProps}>
							<StepLabel {...labelProps}></StepLabel>
						</Step>
					);
				})}
			</Stepper> */}
			{activeStep === steps.length ? (
				<React.Fragment>
					<Typography sx={{ mt: 2, mb: 1 }}>All steps completed - you&apos;re finished</Typography>
					<Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
						<Box sx={{ flex: "1 1 auto" }} />
						<Button onClick={handleReset}>Reset</Button>
					</Box>
				</React.Fragment>
			) : (
				<div>
					<Typography
						variant="h4"
						color={"primary"}
						style={{
							marginBottom: "20px",
							textAlign: "center",
							padding: "25px",
							borderRadius: "10px",
							backgroundColor: "#eff7f9",
							fontWeight: "bold",
						}}
					>
						{steps[activeStep].label}
					</Typography>
					<div className="formContainer">{steps[activeStep].component}</div>
					{/* <Typography sx={{ mt: 2, mb: 1 }}>{steps[activeStep].component}</Typography> */}
					{/* {renderStepperControls()} */}
				</div>
			)}
		</Box>
	);
}
